/**
 * 知识库规则管理（P1 第六章 L2 规则级治理）
 * 布局按编码架构规范 5.9：search-bar + content-card（外层页面骨架由 index.tsx 提供）
 * - 状态 Tag 只用 antd 预设语义色（规范 5.9.2），颜色一律 --au-* 变量
 * - 核心不变式：同 项目+页面+字段 至多一条生效（ACTIVE）；冲突绝不二选一，人工消解
 * - 人工条目优先级最高，自动抽取不得覆盖
 * - P2：「影响」反查引用该规则的历史问答（变更影响提示）+ 按生效规则一键生成 AI 用例
 */
import { useCallback, useEffect, useState } from 'react'
import {
  Button, DatePicker, Drawer, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import {
  type KBDocument, type KBRuleEntry, type KBRuleQaLog, activateRule, extractRules,
  generateCasesFromRules, getDocuments, getRuleQaLogs, getRules,
  resolveRuleConflict, revokeRule, saveRule, updateRule,
} from '@/api/knowledgeBase'

const { Text } = Typography

/** 规则状态：Tag 只用 antd 预设语义色 */
const RULE_STATUS_META: Record<number, { color: string; label: string }> = {
  1: { color: 'success', label: '生效中' },
  2: { color: 'default', label: '被覆盖' },
  3: { color: 'default', label: '已作废' },
  4: { color: 'error', label: '冲突待处理' },
}

function StatusCell({ row }: { row: KBRuleEntry }) {
  const meta = RULE_STATUS_META[row.status] || RULE_STATUS_META[2]
  const tip = row.status === 2 && row.superseded_by > 0
    ? `已被规则 #${row.superseded_by} 覆盖`
    : row.status === 4
      ? (row.note || '同字段存在多条不同规则，问答时将显式拒答，需人工消解')
      : row.note
  return (
    <Tooltip title={tip}>
      <Space size={4}>
        <Tag color={meta.color} style={{ margin: 0 }}>{meta.label}</Tag>
        {row.is_manual && <Tag color="geekblue" style={{ margin: 0 }}>人工</Tag>}
      </Space>
    </Tooltip>
  )
}

/** 规则影响分析抽屉（P2 变更影响提示）：反查引用过该规则的历史问答，规则变更/作废前先看影响面 */
function ImpactDrawer({ rule, onClose }: { rule: KBRuleEntry | null; onClose: () => void }) {
  const [logs, setLogs] = useState<KBRuleQaLog[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const pageSize = 5

  useEffect(() => {
    if (!rule) { setLogs([]); setTotal(0); setPage(1); return }
    setLoading(true)
    getRuleQaLogs(rule.id, { page, page_size: pageSize })
      .then((res) => { setLogs(res.data || []); setTotal(res.total) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [rule, page])

  const columns: ColumnsType<KBRuleQaLog> = [
    { title: '问题', dataIndex: 'question', ellipsis: true },
    {
      title: '答案', dataIndex: 'answer', width: 220, ellipsis: true,
      render: (v: string) => <Tooltip title={v} placement="topLeft"><span>{v || '-'}</span></Tooltip>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>状态</div>, dataIndex: 'is_stale', width: 190,
      render: (_v, r) => (
        <Space size={4} wrap style={{ display: 'flex', justifyContent: 'center' }}>
          {r.is_refused && <Tag color="default" style={{ margin: 0 }}>已拒答</Tag>}
          {r.is_stale && (
            <Tooltip title={r.stale_reason || '规则已变更，答案可能过期'}>
              <Tag color="warning" style={{ margin: 0 }}>已过期</Tag>
            </Tooltip>
          )}
          {r.is_cached && <Tag color="cyan" style={{ margin: 0 }}>缓存</Tag>}
          {r.feedback === 1 && <Tag color="success" style={{ margin: 0 }}>有用</Tag>}
          {r.feedback === 2 && <Tag color="error" style={{ margin: 0 }}>无用</Tag>}
        </Space>
      ),
    },
    {
      title: '时间', dataIndex: 'create_time', width: 150,
      render: (v: string) => (v ? <span style={{ whiteSpace: 'nowrap' }}>{new Date(v).toLocaleString('zh-CN')}</span> : '-'),
    },
  ]

  return (
    <Drawer title={rule ? `规则影响分析 - ${rule.field_name}（#${rule.id}）` : '规则影响分析'} width={760}
      rootClassName="aurora-edit-drawer" open={!!rule} onClose={onClose}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        以下为引用过该规则的历史问答。规则修改/消解/作废/重抽后，相关答案会自动标记「已过期」并联动问答缓存失效。
      </Text>
      <Table rowKey="id" size="small" dataSource={logs} columns={columns} loading={loading}
        locale={{ emptyText: '暂无引用该规则的问答记录' }}
        pagination={{ current: page, pageSize, total, showSizeChanger: false,
          showTotal: (t) => `共 ${t} 条`, onChange: (p) => setPage(p) }} />
    </Drawer>
  )
}

/** 新增/修改规则弹窗（修改时页面/字段可留空保持不变） */
function RuleFormModal({ open, projectId, editing, onClose, onSaved }: {
  open: boolean
  projectId: number
  editing: KBRuleEntry | null
  onClose: () => void
  onSaved: () => void
}) {
  const [form] = Form.useForm()
  const [docs, setDocs] = useState<KBDocument[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    form.resetFields()
    if (editing) {
      form.setFieldsValue({
        page_name: editing.page_name, field_name: editing.field_name,
        rule_text: editing.rule_text, effective_date: editing.effective_date, note: editing.note,
      })
    }
    getDocuments({ project_id: projectId, page: 1, page_size: 100 }).then((res) => setDocs(res.data)).catch(() => {})
  }, [open, editing, projectId, form])

  const handleSave = async () => {
    const vals = await form.validateFields()
    setSaving(true)
    try {
      const eff: string = (vals.effective_date as Dayjs | undefined)?.format('YYYY-MM-DD') || ''
      if (editing) {
        const res = await updateRule(editing.id, {
          rule_text: vals.rule_text, page_name: vals.page_name, field_name: vals.field_name, note: vals.note,
        })
        message.success(res.stale_qa ? `已更新，${res.stale_qa} 条历史答案已标记过期` : '规则已更新并生效')
      } else {
        await saveRule({
          project_id: projectId, page_name: vals.page_name, field_name: vals.field_name,
          rule_text: vals.rule_text, source_doc_id: vals.source_doc_id || 0,
          effective_date: eff, note: vals.note,
        })
        message.success('规则已保存并生效')
      }
      onSaved()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={editing ? `修改规则 #${editing.id}` : '新增人工规则'}
      open={open} onCancel={onClose} onOk={handleSave} confirmLoading={saving}
      okText={editing ? '保存' : '新增'} cancelText="取消" width={560} destroyOnClose
      wrapClassName="aurora-modal-wrap" className="aurora-modal">
      <Form form={form} layout="vertical" style={{ marginTop: 4 }}>
        <Space size="middle" style={{ display: 'flex' }}>
          <Form.Item label="页面/模块" name="page_name" style={{ width: 240 }}
            rules={[{ required: !editing, message: editing ? undefined : '请输入页面名' }]}>
            <Input placeholder="如 订单创建页" maxLength={128} />
          </Form.Item>
          <Form.Item label="字段名" name="field_name" style={{ width: 240, marginBottom: editing ? undefined : 24 }}
            rules={[{ required: !editing, message: editing ? undefined : '请输入字段名' }]}>
            <Input placeholder="文档原文写法" maxLength={128} />
          </Form.Item>
        </Space>
        <Form.Item label="规则内容" name="rule_text" rules={[{ required: true, message: '请输入规则内容' }]}>
          <Input.TextArea placeholder="如 必填，长度 1-50，仅支持中文与字母" autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} />
        </Form.Item>
        {!editing && (
          <Form.Item label="来源文档（可选，人工经验可不选）" name="source_doc_id">
            <Select allowClear placeholder="选择来源文档" showSearch optionFilterProp="label"
              options={docs.map((d) => ({ value: d.id, label: `${d.doc_name} ${d.doc_version}` }))} />
          </Form.Item>
        )}
        <Space size="middle" style={{ display: 'flex' }}>
          <Form.Item label="生效日期" name="effective_date" style={{ width: 240 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="备注" name="note" style={{ width: 240 }}>
            <Input placeholder="选填" maxLength={200} />
          </Form.Item>
        </Space>
        {editing && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            修改后自动置为人工条目（自动抽取不得覆盖），并联动引用旧内容的历史答案标记过期
          </Text>
        )}
      </Form>
    </Modal>
  )
}

export function RulesPanel({ projectId }: { projectId: number }) {
  const [rules, setRules] = useState<KBRuleEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [inputKeyword, setInputKeyword] = useState('')
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<KBRuleEntry | null>(null)
  const [impactRule, setImpactRule] = useState<KBRuleEntry | null>(null)
  const [genLoading, setGenLoading] = useState(false)
  // 勾选的规则条目：按规则生成用例以勾选为准（已生成过用例的规则执行时自动跳过）
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  // 二次确认弹窗受控开关：未勾选/全部已生成时不弹窗、直接提示
  const [genConfirmOpen, setGenConfirmOpen] = useState(false)

  const fetchRules = useCallback(async (p: number, ps: number, kw: string, st?: number) => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await getRules({ project_id: projectId, page: p, page_size: ps, keyword: kw, status: st })
      setRules(res.data); setTotal(res.total)
      setSelectedRowKeys([]) // 列表刷新后重置勾选，避免残留失效选择
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => { fetchRules(page, pageSize, keyword, status) }, [fetchRules, page, pageSize, keyword, status])

  if (!projectId) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--au-text-faint)' }}>请先在顶部选择项目</div>

  const conflictCount = rules.filter((r) => r.status === 4).length

  const handleResolve = (r: KBRuleEntry) => async () => {
    const res = await resolveRuleConflict(r.id)
    message.success(`冲突已消解：保留 #${res.kept_id} 为生效，${res.superseded_count} 条同键条目置为被覆盖`)
    fetchRules(page, pageSize, keyword, status)
  }

  const handleRevoke = (r: KBRuleEntry) => async () => {
    const res = await revokeRule(r.id)
    message.success(res.stale_qa ? `已作废，${res.stale_qa} 条历史答案已标记过期` : '规则已作废')
    fetchRules(page, pageSize, keyword, status)
  }

  const handleActivate = (r: KBRuleEntry) => async () => {
    try {
      const res = await activateRule(r.id)
      message.success(res.superseded_count > 0
        ? `已恢复生效，同键 ${res.superseded_count} 条生效条目自动置为被覆盖`
        : '规则已恢复生效')
      fetchRules(page, pageSize, keyword, status)
    } catch { /* request 拦截器已提示 */ }
  }

  const handleReextract = (r: KBRuleEntry) => async () => {
    message.loading({ content: '正在重新抽取规则…', key: 'extract', duration: 0 })
    try {
      const res = await extractRules(projectId, r.source_doc_id)
      message.success({ content: `抽取完成：新增/更新 ${res.extracted} 条，冲突 ${res.conflicts} 条（回滚旧批次 ${res.rolled_back} 条）`, key: 'extract' })
      fetchRules(page, pageSize, keyword, status)
    } catch {
      message.error({ content: '抽取失败，请确认已配置 AI 且文档为就绪状态', key: 'extract' })
    }
  }

  // 点击按钮先校验勾选：未勾选/全部已生成时直接提示，不弹二次确认；通过才弹确认框
  const handleGenButtonClick = () => {
    const selected = rules.filter((r) => selectedRowKeys.includes(r.id))
    if (selected.length === 0) {
      message.warning('请先在列表中勾选要生成用例的规则')
      return
    }
    if (!selected.some((r) => !r.cases_generated)) {
      message.warning('所选规则均已生成过用例，不允许重复生成')
      return
    }
    setGenConfirmOpen(true)
  }

  // P2 规则联动 AI 用例生成（按勾选执行）：仅对未生成过用例的选中规则生成，已生成的自动跳过防重复
  const handleGenCases = async () => {
    const selected = rules.filter((r) => selectedRowKeys.includes(r.id))
    if (selected.length === 0) {
      message.warning('请先在列表中勾选要生成用例的规则')
      return
    }
    const valid = selected.filter((r) => !r.cases_generated)
    if (valid.length === 0) {
      message.warning('所选规则均已生成过用例，不允许重复生成')
      return
    }
    setGenLoading(true)
    message.loading({ content: '正在按选中规则生成用例，LLM 生成较慢请稍候…', key: 'gencases', duration: 0 })
    try {
      const res = await generateCasesFromRules(projectId, valid.map((r) => r.id))
      const skipped = selected.length - valid.length
      message.success({
        content: `已按 ${res.rule_count} 条规则生成 ${res.total_cases} 条校验用例${skipped > 0 ? `（自动跳过 ${skipped} 条已生成规则）` : ''}，可到「AI 用例生成」页查看`,
        key: 'gencases',
      })
      // 刷新列表以更新「是否生成用例」列（fetchRules 同时会清空勾选）
      fetchRules(page, pageSize, keyword, status)
    } catch {
      message.error({ content: '生成失败：请确认已配置 AI 且所选规则有效', key: 'gencases' })
    } finally {
      setGenLoading(false)
    }
  }

  const columns: ColumnsType<KBRuleEntry> = [
    {
      title: '字段规则', // 主列自动伸缩；规则内容换行完整展示（不截断）
      render: (_v, r) => (
        <div style={{ minWidth: 0 }}>
          <Space size={6} wrap>
            {r.page_name && <Tag color="geekblue" style={{ margin: 0 }}>{r.page_name}</Tag>}
            <Text strong style={{ color: 'var(--au-slate)' }}>{r.field_name}</Text>
          </Space>
          <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{r.rule_text}</div>
        </div>
      ),
    },
    {
      // 是否生成用例：按规则生成用例成功后由后端回写，防止重复生成
      title: <div style={{ textAlign: 'center' }}>是否生成用例</div>, dataIndex: 'cases_generated', width: 116,
      render: (v: boolean) => <Tag color={v ? 'success' : 'default'} style={{ margin: 0 }}>{v ? '已生成' : '未生成'}</Tag>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>状态</div>, dataIndex: 'status', width: 150,
      render: (_v, r) => <StatusCell row={r} />,
    },
    {
      title: <div style={{ textAlign: 'center' }}>来源</div>, dataIndex: 'source_doc_name', width: 220, ellipsis: true,
      render: (v: string, r) => (
        <div>
          <div style={{ fontSize: 12 }}>{v || (r.source_doc_id > 0 ? `文档 #${r.source_doc_id}` : '人工经验')}</div>
          <div style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>
            {r.doc_version && r.doc_version !== 'MANUAL' ? r.doc_version : ''}
            {r.effective_date ? ` · ${r.effective_date}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: '创建时间', dataIndex: 'create_time', width: 200,
      render: (v: string) => (v ? <span style={{ whiteSpace: 'nowrap' }}>{new Date(v).toLocaleString('zh-CN')}</span> : '-'),
    },
    {
      title: '操作', width: 320, fixed: 'right',
      render: (_v, r) => (
        <Space size={0} wrap={false}>
          <Button type="link" size="small" onClick={() => setImpactRule(r)}>影响</Button>
          {r.status !== 3 && (
            <Button type="link" size="small" onClick={() => { setEditing(r); setModalOpen(true) }}>修改</Button>
          )}
          {r.status === 4 && (
            <Popconfirm title="保留此条为生效，同键其余条目置为被覆盖，确定？" okText="确定" cancelText="取消"
              onConfirm={() => handleResolve(r)()}>
              <Button type="link" size="small">消解</Button>
            </Popconfirm>
          )}
          {(r.status === 2 || r.status === 3) && (
            <Popconfirm title="恢复为生效，同键其余生效条目将自动置为被覆盖，确定？" okText="确定" cancelText="取消"
              onConfirm={() => handleActivate(r)()}>
              <Button type="link" size="small">恢复生效</Button>
            </Popconfirm>
          )}
          {!r.is_manual && r.source_doc_id > 0 && (
            <Popconfirm title="重抽将回滚该文档最近一批自动规则后重新抽取，确定？" okText="确定" cancelText="取消"
              onConfirm={() => handleReextract(r)()}>
              <Button type="link" size="small">重抽</Button>
            </Popconfirm>
          )}
          {r.status !== 3 && (
            <Popconfirm title="作废后该规则不再参与问答，确定？" okText="确定" cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleRevoke(r)()}>
              <Button type="link" size="small" danger>作废</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <>
      {/* 搜索栏：状态/关键词筛选 + 冲突提醒 + 新增 */}
      <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Select placeholder="状态" allowClear style={{ width: 140 }} value={status}
          onChange={(v) => { setPage(1); setStatus(v) }}
          options={Object.entries(RULE_STATUS_META).map(([k, m]) => ({ value: Number(k), label: m.label }))} />
        <Space.Compact>
          <Input placeholder="搜索页面/字段/规则内容" allowClear style={{ minWidth: 220 }}
            value={inputKeyword} onChange={(e) => setInputKeyword(e.target.value)}
            onPressEnter={() => { setPage(1); setKeyword(inputKeyword.trim()) }} />
          <Button type="primary" onClick={() => { setPage(1); setKeyword(inputKeyword.trim()) }}
            style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}>
            查询
          </Button>
        </Space.Compact>
        <Text type="secondary" style={{ fontSize: 12 }}>
          操作随状态变化：生效中=修改/作废 · 冲突=修改/消解/作废 · 被覆盖/已作废=恢复生效 · 重抽仅限文档抽取条目 · 影响可查引用该规则的历史问答 · 勾选规则后点「按规则生成用例」（已生成过的不可重复生成）
        </Text>
        {conflictCount > 0 && (
          <Tooltip title="存在未消解的规则冲突，相关问题的问答将被显式拒答">
            <Tag color="error" style={{ cursor: 'default' }}>{conflictCount} 条冲突待处理</Tag>
          </Tooltip>
        )}
        {/* 按勾选的规则生成用例：一条/多条均可；已生成过用例的规则执行时自动跳过（不允许重复生成） */}
        <Popconfirm
          open={genConfirmOpen}
          title={(() => {
            const valid = rules.filter((r) => selectedRowKeys.includes(r.id) && !r.cases_generated).length
            return `按勾选的 ${valid} 条规则生成校验类用例${selectedRowKeys.length > valid ? `（自动跳过 ${selectedRowKeys.length - valid} 条已生成规则）` : ''}，写入「AI 用例生成」模块，确定？`
          })()}
          okText="确定" cancelText="取消"
          onConfirm={() => { setGenConfirmOpen(false); void handleGenCases() }}
          onCancel={() => setGenConfirmOpen(false)}>
          <Button loading={genLoading} style={{ marginLeft: 'auto' }} onClick={handleGenButtonClick}>
            按规则生成用例
          </Button>
        </Popconfirm>
        <Button type="primary" style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)' }}
          onClick={() => { setEditing(null); setModalOpen(true) }}>
          新增规则
        </Button>
      </div>

      {/* 内容卡片 */}
      <div className="content-card">
        <Table rowKey="id" loading={loading} dataSource={rules} columns={columns} size="middle"
          rowSelection={{ selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys), columnWidth: 48, fixed: 'left' }}
          scroll={{ x: 1100, y: '100%' }} tableLayout="fixed"
          locale={{ emptyText: '暂无规则条目，上传文档后自动抽取，或点击右上角「新增规则」人工录入' }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) },
          }} />
      </div>

      <RuleFormModal open={modalOpen} projectId={projectId} editing={editing}
        onClose={() => setModalOpen(false)} onSaved={() => fetchRules(page, pageSize, keyword, status)} />

      <ImpactDrawer rule={impactRule} onClose={() => setImpactRule(null)} />
    </>
  )
}
